import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useUserSubscription } from "../../../core_stores/useSubscriptionStore";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
} from "../../../view_stores/chat_messages/chatModels";
import {
  useChatElements,
  useChatException,
} from "../../../view_stores/chat_messages/useChatMessagesStore";
import {
  ALLOWED_IMAGE_TYPES,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import { useIsRollbackError } from "../../../view_stores/useRollbackErrorHandlerStore";
import { useShouldConfirmRollback } from "../../../view_stores/useRollbackHandlerStore";
import { useCanHandleTechnicalError } from "../../../view_stores/useTechnicalErrorHandlerStore";
import ImageDropArea from "../../common_components/ImageDropArea";
import ProjectChatMessages from "./ProjectChatMessages";
import { ProjectChatTextField } from "./ProjectChatTextField";
import ProjectOutOfTokensMessage from "./ProjectOutOfTokensMessage";
import ProjectRollbackConfirmation from "./ProjectRollbackConfirmation";
import ProjectRollbackErrorActions from "./ProjectRollbackErrorActions";
import ProjectTechnicalErrorActions from "./ProjectTechnicalErrorActions";

export function ProjectChat() {
  const exception = useChatException();
  const isCompactView = useIsCompactView();
  const { setAttachedImage } = usePromptInputActions();
  const { showSnackbar } = useSnackbar();
  const [isDragging, setIsDragging] = useState(false);
  const [isValidFileType, setIsValidFileType] = useState<boolean>(true);
  const dragCounter = useRef(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.types.includes("Files")) {
      const items = e.dataTransfer?.items;
      if (items?.length === 1) {
        const item = items[0];
        setIsValidFileType(ALLOWED_IMAGE_TYPES.includes(item.type));
      }
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsValidFileType(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    setIsValidFileType(true);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar("Unsupported file type", {
        type: "error",
      });
    }
  };

  return (
    <div
      ref={chatContainerRef}
      className="h-full flex-1 flex flex-col overflow-hidden relative bg-dark-surface-secondary"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <ProjectChatMessages />
      </div>
      {exception && (
        <div className="text-red-700 text-center pb-4">{exception.message}</div>
      )}

      <div
        className={`
          ${isCompactView ? "pl-4" : ""}
        px-2 pt-4 pb-4 min-h-[140px]`}
      >
        <PromptArea />
      </div>
      {isDragging && <ImageDropArea isValidFileType={isValidFileType} />}
      <Tooltip
        id="chat-tooltip"
        className="max-w-[90%]"
        place="top"
        opacity={1.0}
      />
    </div>
  );
}

function PromptArea() {
  const chatElements = useChatElements();
  const canHandleTechnicalError = useCanHandleTechnicalError();
  const isRollbackError = useIsRollbackError();
  const shouldConfirmRollback = useShouldConfirmRollback();
  const subscription = useUserSubscription();

  const [isEditingPrompt, setIsEditingPrompt] = useState(false);

  useEffect(() => {
    if (!canHandleTechnicalError) {
      setIsEditingPrompt(false);
    }
  }, [canHandleTechnicalError]);

  const areAllMessagesFullyDisplayed = chatElements.every((msg) => {
    if (msg instanceof ProjectChatAssistantMessage) {
      return msg.animationStatus === AssistantMessageAnimationStatus.DISPLAYED;
    }
    return true;
  });

  // Show out of tokens banner when user has no tokens remaining
  // Round to integer to treat fractional tokens (< 1) as zero
  if (subscription && Math.round(subscription.remaining) <= 0) {
    return <ProjectOutOfTokensMessage />;
  }

  if (isRollbackError) {
    return <ProjectRollbackErrorActions />;
  }

  if (shouldConfirmRollback) {
    return <ProjectRollbackConfirmation />;
  }

  if (canHandleTechnicalError && !isEditingPrompt && areAllMessagesFullyDisplayed) {
    return (
      <ProjectTechnicalErrorActions onEdit={() => setIsEditingPrompt(true)} />
    );
  }

  return <ProjectChatTextField />;
}
